import { Column } from '../../../grids';
import { DrawApi } from '@apply/api';
import { Dispatch, ReactElement, SetStateAction } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';
import React from 'react';
import { calculateBalance } from '../functions';
import { EmailBorrowerToResubmitButton } from '../buttons/EmailBorrowerToResubmitButton';
import { formatCurrencyConditional } from '../totalItems/constants/constants';

interface IDrawManualColumnProps {
    drawId: number;
    drawNumber: number;
    drawFinalized: boolean;
    latestDrawHasFunding: boolean;
    toggleArray: boolean[];
    applicationInsights: ApplicationInsights;
    set_drawBudget: Dispatch<SetStateAction<DrawApi.IDrawBudget | undefined>>;
    isReadOnly?: boolean;
}

export const DrawManualColumn = ({
    drawId,
    drawNumber,
    drawFinalized,
    latestDrawHasFunding,
    toggleArray,
    applicationInsights,
    set_drawBudget,
    isReadOnly = false,
}: IDrawManualColumnProps): ReactElement[] => {
    const drawApi = new DrawApi.DrawApi(applicationInsights);

    const calculateOutOfBalance = (data: DrawApi.IDrawBudgetItem) => {
        return calculateBalance(drawNumber, data) ?? '';
    };

    function copyBorrowerRequestToFunded(drawApi: DrawApi.DrawApi, drawId: number) {
        drawApi.copyBorrowerRequestToFunded(drawId).then((data) => set_drawBudget(data.result));
    }

    const fundedColumnHeader = () => {
        return (
            <div style={{ width: 90, textAlign: 'center' }}>
                <p>FUNDED</p>
                <button
                    type='button'
                    onClick={() => copyBorrowerRequestToFunded(drawApi, drawId)}
                    className='button hollow'
                    title={
                        isReadOnly ? 'Unable to Copy From Borrower Request - Budget is Read Only' : 'Copy from Borrower Request'
                    }
                    disabled={isReadOnly}
                >
                    <FontAwesomeIcon icon={faCopy} />
                </button>
            </div>
        );
    };

    const inspectionColumnHeader = () => {
        return (
            <div style={{ width: 90, textAlign: 'center' }}>
                <p>INSPECTION</p>
                {!drawFinalized && EmailBorrowerToResubmitButton({ drawId, latestDrawHasFunding, applicationInsights })}
            </div>
        );
    };

    if (toggleArray[drawNumber] === true) {
        return [
            <Column
                name={`ManualBorrowerRequest${drawId}-${drawNumber}`}
                key={`ManualBorrowerRequest${drawId}-${drawNumber}`}
                dataType='number'
                editorOptions={{
                    dataType: 'number',
                    format: { type: 'currency', precision: 2 },
                    step: 0,
                }}
                dataField={`drawItems-borrowerRequest-${drawId}-${drawNumber}`}
                cellRender={(cellData) => {
                    const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                    return formatCurrencyConditional(dataItem?.borrowerRequest);
                }}
                caption='BORROWER REQUEST'
                format={'currency'}
                allowResizing={false}
                minWidth={100}
                alignment={'right'}
                allowEditing={!drawFinalized}
                width={100}
            />,
            <Column
                name={`ManualFunded${drawId}-${drawNumber}`}
                key={`ManualFunded${drawId}-${drawNumber}`}
                dataType='number'
                editorOptions={{
                    dataType: 'number',
                    format: { type: 'currency', precision: 2 },
                    step: 0,
                }}
                headerCellRender={fundedColumnHeader}
                dataField={`drawItems-funded-${drawId}-${drawNumber}`}
                cellRender={(cellData) => {
                    const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                    return formatCurrencyConditional(dataItem?.funded);
                }}
                caption='FUNDED'
                format={'currency'}
                allowResizing={false}
                minWidth={100}
                alignment={'right'}
                allowEditing={!drawFinalized}
                width={100}
            />,
            <Column
                name={`ManualInspection${drawId}-${drawNumber}`}
                key={`ManualInspection${drawId}-${drawNumber}`}
                dataType='number'
                editorOptions={{
                    dataType: 'number',
                    format: { type: 'currency', precision: 2 },
                    step: 0,
                }}
                headerCellRender={inspectionColumnHeader}
                dataField={`drawItems-inspection-${drawId}-${drawNumber}`}
                cellRender={(cellData) => {
                    const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                    return formatCurrencyConditional(dataItem?.inspection);
                }}
                caption='INSPECTION'
                format={'currency'}
                allowResizing={false}
                minWidth={100}
                alignment={'right'}
                allowEditing={!drawFinalized}
                width={100}
            />,
            <Column
                name={`OutOfBalance${drawId}-${drawNumber}`}
                key={`OutOfBalance${drawId}-${drawNumber}`}
                calculateCellValue={calculateOutOfBalance}
                cellRender={(cellData) => formatCurrencyConditional(cellData.value)}
                caption='OUT OF BALANCE'
                format={'currency'}
                allowResizing={false}
                minWidth={125}
                alignment={'right'}
                allowEditing={false}
                width={125}
            />,
            <Column
                name={`ManualNotes${drawId}-${drawNumber}`}
                key={`ManualNotes${drawId}-${drawNumber}`}
                dataField={`drawItems-notes-${drawId}-${drawNumber}`}
                cellRender={(cellData) => {
                    const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                    return dataItem?.notes;
                }}
                caption='NOTES'
                format={'string'}
                allowResizing={false}
                minWidth={275}
                allowEditing={!drawFinalized}
                width={275}
            />,
        ];
    } else {
        if (drawFinalized) {
            return [
                <Column
                    name={`Column${drawId}-${drawNumber}`}
                    key={`Column${drawId}-${drawNumber}`}
                    dataField={`drawItems-funded-${drawId}-${drawNumber}`}
                    cellRender={(cellData) => {
                        const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                        return formatCurrencyConditional(dataItem?.funded);
                    }}
                    allowEditing={false}
                    caption={`DRAW #${drawNumber + 1}`}
                    format={'currency'}
                    allowResizing={false}
                    alignment={'right'}
                    minWidth={100}
                    width={100}
                />,
            ];
        } else {
            return [
                <Column
                    key={`Column${drawId}-${drawNumber}`}
                    name={`Column${drawId}-${drawNumber}`}
                    dataField={`drawItems-borrowerRequest-${drawId}-${drawNumber}`}
                    cellRender={(cellData) => {
                        const dataItem = cellData.data?.drawItems?.find((item) => item.drawNumber == drawNumber);
                        return formatCurrencyConditional(dataItem?.borrowerRequest);
                    }}
                    allowEditing={false}
                    caption={`DRAW #${drawNumber + 1}`}
                    format={'currency'}
                    allowResizing={false}
                    alignment={'right'}
                    minWidth={100}
                    width={100}
                />,
            ];
        }
    }
};
